// src/pages/Workforce/LeaveRequestsPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import T from '../../components/common/T';
import { useAuth } from '../../context/AuthContext';
import { canManageWorkforce, getWorkforceId } from '../../utils/roleUtils';
import DataTable from '../../components/common/DataTables'; // 🟢 IMPORTED DATATABLE
import api from '../../api/axiosConfig';
import PageLayout, { PageHeader, StatsGrid, Toolbar, TableCard } from '../../components/common/PageLayout';
import { TopPrimaryButton } from '../../components/common/StandardButtons';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  CalendarDays, CheckCircle, Clock, XCircle, Search,
  RefreshCw, Eye, Check, X as RejectIcon, ArrowLeft,
  Loader2, Plus, AlertCircle, Edit
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Request Leave Form (Full Page)
// ─────────────────────────────────────────────────────────────────────────────
function RequestLeaveForm({ myWorkforceId, leaveBalance, onClose, onSuccess }) {
  const [form, setForm] = useState({ leave_type: 'Casual', start_date: '', end_date: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError,  setSubmitError]  = useState(null);

  const totalDays = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    const start = new Date(form.start_date);
    const end   = new Date(form.end_date);
    if (end < start) return 0;
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [form.start_date, form.end_date]);

  const balanceForType = useMemo(() => {
    if (!leaveBalance) return null;
    const key = form.leave_type.toLowerCase();
    return {
      total:     leaveBalance[`${key}_total`]     ?? 0,
      used:      leaveBalance[`${key}_used`]      ?? 0,
      remaining: leaveBalance[`${key}_remaining`] ?? 0
    };
  }, [form.leave_type, leaveBalance]);

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date || !form.reason.trim()) {
      setSubmitError('Please fill in all fields.');
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setSubmitError('End date cannot be before start date.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/workforce/leaves/request', { workforce_id: myWorkforceId, ...form });
      onSuccess();
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to submit leave request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 w-full max-w-4xl mx-auto flex flex-col">
      <button onClick={onClose} className="flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900 transition mb-6 w-fit">
        <ArrowLeft size={16} /> <T>Back to My Requests</T>
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
        <div className="px-6 py-5 border-b border-gray-100 bg-white">
          <h2 className="text-xl font-bold text-gray-900"><T>Request Leave</T></h2>
          <p className="text-sm text-gray-500 mt-1">Submit a new leave application for approval.</p>
        </div>

        <div className="p-6 md:p-8 space-y-6 flex-1">
          {balanceForType && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm shadow-sm">
              <AlertCircle size={18} className="text-blue-600 shrink-0" />
              <span className="text-blue-800">
                <span className="font-bold">{form.leave_type} Leave:</span> {balanceForType.remaining} days remaining
                <span className="text-blue-600 ml-1">({balanceForType.used} used of {balanceForType.total})</span>
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>Leave Type</T></label>
            <select value={form.leave_type} onChange={(e) => setForm(f => ({ ...f, leave_type: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 transition">
              <option value="Casual">Casual Leave</option>
              <option value="Sick">Sick Leave</option>
              <option value="Earned">Earned Leave</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>Start Date</T></label>
              <input type="date" value={form.start_date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium outline-none focus:ring-2 focus:ring-blue-500 transition" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>End Date</T></label>
              <input type="date" value={form.end_date} min={form.start_date || new Date().toISOString().split('T')[0]} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium outline-none focus:ring-2 focus:ring-blue-500 transition" />
            </div>
          </div>

          {totalDays > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-bold text-gray-700 border border-gray-200">
              <CalendarDays size={16} className="text-gray-500" />
              {totalDays} {totalDays === 1 ? 'Day' : 'Days'}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 mb-2"><T>Reason</T></label>
            <textarea rows={4} placeholder="Briefly describe the reason for your leave..." value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 transition resize-none" />
          </div>

          {submitError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-lg font-medium shadow-sm">{submitError}</div>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button onClick={onClose} className="px-6 py-2.5 rounded-lg border border-gray-300 text-sm font-bold text-gray-700 hover:bg-gray-100 transition shadow-sm">Cancel</button>
          <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition disabled:opacity-60 shadow-sm">
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function LeaveRequestsPage() {
  const { user } = useAuth();
  const isAdmin       = canManageWorkforce(user);
  const myWorkforceId = getWorkforceId(user);

  const [leaves, setLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);

  const [adminTab, setAdminTab] = useState('requests');
  const [balances, setBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balanceSearchTerm, setBalanceSearchTerm] = useState("");
  const [editingBalanceId, setEditingBalanceId] = useState(null);
  const [editBalanceForm, setEditBalanceForm] = useState({});

  const selectedLeave = leaves.find(l => l.id === selectedLeaveId);

  const fetchLeaveRequests = useCallback(async () => {
    try {
      setIsLoading(true); setError(null);
      const url = isAdmin ? '/workforce/leaves/requests' : `/workforce/leaves/requests?workforce_id=${myWorkforceId}`;
      const response = await api.get(url);
      const dbData = response.data || [];
      setLeaves(dbData.map(item => ({
        id: item.id,
        employeeName: item.employee_name,
        role: item.department || 'Staff',
        avatar: item.employee_name ? item.employee_name.charAt(0).toUpperCase() : 'U',
        type: item.leave_type,
        startDate: new Date(item.start_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
        endDate: new Date(item.end_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
        duration: `${item.total_days} Days`,
        appliedOn: new Date(item.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
        status: item.status,
        reason: item.reason || "No specific reason provided.",
        balance: { total: "-", used: "-", remaining: "-" }
      })));
    } catch (err) { setError(err.response?.data?.error || err.message); } finally { setIsLoading(false); setIsRefreshing(false); }
  }, [isAdmin, myWorkforceId]);

  const fetchLeaveBalance = useCallback(async () => {
    if (!myWorkforceId) return;
    try {
      const res = await api.get(`/workforce/leaves/balance?workforce_id=${myWorkforceId}`);
      if (res.data) setLeaveBalance(res.data);
    } catch (err) { console.error(err); }
  }, [myWorkforceId]);

  const fetchAllBalances = useCallback(async () => {
    setLoadingBalances(true);
    try {
      const res = await api.get('/workforce/leaves/all-balances');
      if (res.data) setBalances(res.data);
    } catch (err) { console.error(err); } finally { setLoadingBalances(false); }
  }, []);

  useEffect(() => {
    fetchLeaveRequests();
    if (!isAdmin) fetchLeaveBalance();
  }, [fetchLeaveRequests, fetchLeaveBalance, isAdmin]);

  useEffect(() => {
    if (isAdmin && adminTab === 'balances' && balances.length === 0) fetchAllBalances();
  }, [adminTab, isAdmin, balances.length, fetchAllBalances]);

  const handleUpdateStatus = async (e, id, newStatus) => {
    if (e) e.stopPropagation();
    const previousLeaves = [...leaves];
    setLeaves(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
    try {
      await api.put(`/workforce/leaves/${id}`, { status: newStatus, rejection_reason: newStatus === 'Rejected' ? 'Rejected by Admin' : null });
    } catch (err) { console.error(err); setLeaves(previousLeaves); }
  };

  const handleSaveBalanceEdit = async (workforceId) => {
    try {
      const res = await api.put(`/workforce/leaves/balances/${workforceId}`, editBalanceForm);
      if (res.data) { setEditingBalanceId(null); fetchAllBalances(); }
    } catch (err) { console.error(err); }
  };

  const handleRefresh = () => { if (adminTab === 'requests') { setIsRefreshing(true); fetchLeaveRequests(); } else { fetchAllBalances(); } };

  const filteredData = useMemo(() => leaves.filter(leave => {
    const matchesSearch = isAdmin ? leave?.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) : true;
    const matchesStatus = statusFilter === "All" || leave?.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [searchTerm, statusFilter, leaves, isAdmin]);

  const filteredBalances = useMemo(() => balances.filter(b => b.name.toLowerCase().includes(balanceSearchTerm.toLowerCase())), [balances, balanceSearchTerm]);

  const stats = useMemo(() => ({
    total: leaves.length,
    pending: leaves.filter(l => l.status === "Pending").length,
    approved: leaves.filter(l => l.status === "Approved").length,
    rejected: leaves.filter(l => l.status === "Rejected").length
  }), [leaves]);

  const getStatusStyles = (status) => {
    switch(status) {
      case 'Approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending':  return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Rejected': return 'bg-red-100   text-red-700   border-red-200';
      default:         return 'bg-gray-100  text-gray-700  border-gray-200';
    }
  };

  // 🟢 DATATABLE COLUMNS: Leave Requests
  const requestColumns = useMemo(() => {
    const cols = [
      {
        header: "Employee",
        accessor: "employeeName",
        render: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">
              {row.avatar}
            </div>
            <div>
              <p className="font-bold text-gray-900">{row.employeeName}</p>
              <p className="text-xs text-gray-500 font-medium">{row.role}</p>
            </div>
          </div>
        )
      },
      { header: "Leave Type", accessor: "type", cellClassName: "font-medium text-gray-700" },
      {
        header: "Duration",
        accessor: "duration",
        render: (row) => (
          <div>
            <p className="font-medium text-gray-900 text-sm">{row.startDate} → {row.endDate}</p>
            <p className="text-xs text-gray-500 mt-0.5">{row.duration}</p>
          </div>
        )
      },
      { header: "Applied On", accessor: "appliedOn", cellClassName: "font-medium text-gray-600" },
      {
        header: "Status",
        accessor: "status",
        align: "center",
        render: (row) => (
          <span className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-[10px] font-bold uppercase border tracking-wide min-w-[70px] ${getStatusStyles(row.status)}`}>
            {row.status}
          </span>
        )
      },
      {
        header: "Actions",
        align: "center",
        render: (row) => (
          <div className="flex items-center justify-center gap-1.5">
            <button onClick={(e) => { e.stopPropagation(); setSelectedLeaveId(row.id); }} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition" title="View Full Details">
              <Eye size={16} />
            </button>
            {isAdmin && (
              <>
                <button onClick={(e) => { e.stopPropagation(); handleUpdateStatus(e, row.id, "Approved"); }} disabled={row.status === 'Approved'} className={`p-1.5 rounded-lg transition ${row.status === 'Approved' ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 bg-green-50 hover:bg-green-100'}`} title="Approve">
                  <Check size={16} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); handleUpdateStatus(e, row.id, "Rejected"); }} disabled={row.status === 'Rejected'} className={`p-1.5 rounded-lg transition ${row.status === 'Rejected' ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 bg-red-50 hover:bg-red-100'}`} title="Reject">
                  <RejectIcon size={16} />
                </button>
              </>
            )}
          </div>
        )
      }
    ];
    return cols;
  }, [isAdmin]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // 🟢 DATATABLE COLUMNS: Balances
  const balanceColumns = useMemo(() => [
    {
      header: "Employee Details",
      accessor: "name",
      render: (emp) => (
        <div>
          <p className="font-bold text-gray-900">{emp.name}</p>
          <p className="text-xs text-gray-500">{emp.department} • {emp.employee_id}</p>
        </div>
      )
    },
    {
      header: "Casual Leaves",
      align: "center",
      render: (emp) => {
        const isEditing = editingBalanceId === emp.workforce_id;
        return isEditing ? (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs text-gray-400">Total:</span>
            <input type="number" value={editBalanceForm.casual_total} onChange={e => setEditBalanceForm({...editBalanceForm, casual_total: e.target.value})} className="w-14 p-1 border rounded text-center text-xs outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
        ) : (
          <div>
            <p className="text-sm font-bold text-gray-800">{emp.casual_total - emp.casual_used}</p>
            <p className="text-[10px] text-gray-500">{emp.casual_used} used of {emp.casual_total}</p>
          </div>
        );
      }
    },
    {
      header: "Sick Leaves",
      align: "center",
      render: (emp) => {
        const isEditing = editingBalanceId === emp.workforce_id;
        return isEditing ? (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs text-gray-400">Total:</span>
            <input type="number" value={editBalanceForm.sick_total} onChange={e => setEditBalanceForm({...editBalanceForm, sick_total: e.target.value})} className="w-14 p-1 border rounded text-center text-xs outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
        ) : (
          <div>
            <p className="text-sm font-bold text-gray-800">{emp.sick_total - emp.sick_used}</p>
            <p className="text-[10px] text-gray-500">{emp.sick_used} used of {emp.sick_total}</p>
          </div>
        );
      }
    },
    {
      header: "Earned Leaves",
      align: "center",
      render: (emp) => {
        const isEditing = editingBalanceId === emp.workforce_id;
        return isEditing ? (
          <div className="flex items-center justify-center gap-1">
            <span className="text-xs text-gray-400">Total:</span>
            <input type="number" value={editBalanceForm.earned_total} onChange={e => setEditBalanceForm({...editBalanceForm, earned_total: e.target.value})} className="w-14 p-1 border rounded text-center text-xs outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
        ) : (
          <div>
            <p className="text-sm font-bold text-gray-800">{emp.earned_total - emp.earned_used}</p>
            <p className="text-[10px] text-gray-500">{emp.earned_used} used of {emp.earned_total}</p>
          </div>
        );
      }
    },
    {
      header: "Unpaid Taken",
      align: "center",
      cellClassName: "bg-red-50/40 font-bold",
      render: (emp) => {
        const isEditing = editingBalanceId === emp.workforce_id;
        return isEditing ? (
          <div className="flex items-center justify-center">
            <input type="number" step="0.5" value={editBalanceForm.unpaid_used} onChange={e => setEditBalanceForm({...editBalanceForm, unpaid_used: e.target.value})} className="w-14 p-1 border border-red-300 rounded text-center text-xs font-bold text-red-600 outline-none focus:ring-1 focus:ring-red-500" />
          </div>
        ) : (
          <p className="text-red-600">{emp.unpaid_used}</p>
        );
      }
    },
    {
      header: "Actions",
      align: "center",
      render: (emp) => {
        const isEditing = editingBalanceId === emp.workforce_id;
        return isEditing ? (
          <div className="flex justify-center gap-2">
            <button onClick={() => handleSaveBalanceEdit(emp.workforce_id)} className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition" title="Save"><Check size={16}/></button>
            <button onClick={() => setEditingBalanceId(null)} className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition" title="Cancel"><RejectIcon size={16}/></button>
          </div>
        ) : (
          <button onClick={() => {
            setEditingBalanceId(emp.workforce_id);
            setEditBalanceForm({ casual_total: emp.casual_total, sick_total: emp.sick_total, earned_total: emp.earned_total, unpaid_used: emp.unpaid_used });
          }} className="p-1.5 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition" title="Adjust Balance">
            <Edit size={16} />
          </button>
        );
      }
    }
  ], [editingBalanceId, editBalanceForm]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const adminStats = useMemo(() => [
    {
      label: "Total Requests",
      value: stats.total,
      icon: CalendarDaysIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => setStatusFilter("All"),
    },
    {
      label: "Pending Action",
      value: stats.pending,
      icon: ClockIcon,
      variant: "orange",
      isActive: statusFilter === "Pending",
      onClick: () => setStatusFilter(s => s === "Pending" ? "All" : "Pending"),
    },
    {
      label: "Approved",
      value: stats.approved,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Approved",
      onClick: () => setStatusFilter(s => s === "Approved" ? "All" : "Approved"),
    },
    {
      label: "Rejected",
      value: stats.rejected,
      icon: XCircleIcon,
      variant: "red",
      isActive: statusFilter === "Rejected",
      onClick: () => setStatusFilter(s => s === "Rejected" ? "All" : "Rejected"),
    },
  ], [stats, statusFilter]);

  return (
    <PageLayout>
      {showRequestModal && !isAdmin ? (
        <RequestLeaveForm myWorkforceId={myWorkforceId} leaveBalance={leaveBalance} onClose={() => setShowRequestModal(false)} onSuccess={async () => { setShowRequestModal(false); await fetchLeaveRequests(); await fetchLeaveBalance(); }} />
      ) : (
        <>
          <PageHeader
            icon={CalendarDaysIcon}
            iconColor="text-[#E8612C]"
            title={isAdmin ? 'Leave Management' : 'My Leave Requests'}
            subtitle={isAdmin ? 'Review applications and manage employee quotas' : 'Track your leave history and apply for time off'}
            onRefresh={handleRefresh}
            loading={isRefreshing || isLoading || loadingBalances}
          >
            {isAdmin && !selectedLeave && (
              <div className="flex bg-gray-100 dark:bg-[#21262d] p-0.5 rounded-lg border border-gray-200 dark:border-[#30363d] h-8">
                <button
                  type="button"
                  onClick={() => setAdminTab('requests')}
                  className={`px-3 text-xs font-bold uppercase rounded-md transition cursor-pointer ${
                    adminTab === 'requests'
                      ? 'bg-white dark:bg-[#161b22] text-[#E8612C] shadow-xs border border-gray-200 dark:border-[#30363d]'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Requests
                </button>
                <button
                  type="button"
                  onClick={() => setAdminTab('balances')}
                  className={`px-3 text-xs font-bold uppercase rounded-md transition cursor-pointer ${
                    adminTab === 'balances'
                      ? 'bg-white dark:bg-[#161b22] text-[#E8612C] shadow-xs border border-gray-200 dark:border-[#30363d]'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Balances
                </button>
              </div>
            )}
            {!isAdmin && !selectedLeave && (
              <TopPrimaryButton
                onClick={() => setShowRequestModal(true)}
                label="Request Leave"
              />
            )}
          </PageHeader>

          {error && <div className="bg-red-50 dark:bg-rose-950/40 border border-red-200 dark:border-rose-800/60 text-red-700 dark:text-rose-400 p-4 rounded-lg mb-4 shadow-xs"><span><span className="font-bold">Error:</span> {error}</span></div>}

          {/* ── ADMIN BALANCES VIEW ── */}
          {isAdmin && adminTab === 'balances' ? (
             <div className="flex-1 flex flex-col">
               <Toolbar
                 searchTerm={balanceSearchTerm}
                 onSearch={setBalanceSearchTerm}
                 searchPlaceholder="Search employee by name..."
               />
               
               <TableCard>
                 {loadingBalances ? (
                    <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-blue-500"/></div>
                 ) : (
                    <DataTable 
                      data={filteredBalances} 
                      columns={balanceColumns} 
                      keyExtractor={(item) => item.workforce_id}
                      emptyMessage="No employee balances found."
                    />
                 )}
               </TableCard>
             </div>

          /* ── REQUESTS VIEW ── */
          ) : (
            <>
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center py-24"><Loader2 className="animate-spin text-gray-400" size={40} /></div>
              ) : selectedLeave ? (
                <div className="flex-1">
                  <button onClick={() => setSelectedLeaveId(null)} className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition mb-4 w-fit cursor-pointer">
                    <ArrowLeft size={16} /> <T>Back to {isAdmin ? 'All Requests' : 'My Requests'}</T>
                  </button>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl p-6 shadow-xs flex flex-col items-center text-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 flex items-center justify-center font-bold text-3xl">{selectedLeave.avatar}</div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selectedLeave.employeeName}</h3>
                        <p className="text-sm text-gray-500 dark:text-slate-400">{selectedLeave.role}</p>
                      </div>
                      <div className="w-full border-t border-gray-100 dark:border-[#30363d] pt-4 space-y-3 text-sm text-left">
                        <div><p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Applied On</p><p className="font-semibold text-gray-700 dark:text-slate-200">{selectedLeave.appliedOn}</p></div>
                        <div><p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Duration</p><p className="font-semibold text-gray-700 dark:text-slate-200">{selectedLeave.duration}</p></div>
                      </div>
                      {isAdmin && selectedLeave.status === 'Pending' && (
                        <div className="w-full flex gap-2 pt-2">
                          <button onClick={(e) => { handleUpdateStatus(e, selectedLeave.id, 'Approved'); setSelectedLeaveId(null); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition cursor-pointer"><Check size={15} /> Approve</button>
                          <button onClick={(e) => { handleUpdateStatus(e, selectedLeave.id, 'Rejected'); setSelectedLeaveId(null); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition cursor-pointer"><RejectIcon size={15} /> Reject</button>
                        </div>
                      )}
                    </div>

                    <div className="lg:col-span-2 space-y-4">
                      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl p-6 shadow-xs">
                        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-[#30363d]">
                          <div><p className="text-xs font-bold uppercase text-gray-400 mb-1">Leave Type</p><p className="text-xl font-bold text-gray-900 dark:text-white">{selectedLeave.type}</p></div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${getStatusStyles(selectedLeave.status)}`}>{selectedLeave.status}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-6 text-sm mb-4">
                          <div><p className="text-[10px] font-bold uppercase text-gray-400 mb-1">From</p><p className="font-bold text-gray-900 dark:text-white">{selectedLeave.startDate}</p></div>
                          <div><p className="text-[10px] font-bold uppercase text-gray-400 mb-1">To</p><p className="font-bold text-gray-900 dark:text-white">{selectedLeave.endDate}</p></div>
                        </div>
                        <div><p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Reason</p><p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">{selectedLeave.reason}</p></div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-green-50 dark:bg-emerald-950/30 border border-green-100 dark:border-emerald-800/40 p-4 rounded-xl text-center">
                          <p className="text-3xl font-bold text-green-700 dark:text-emerald-400">{selectedLeave.balance.total}</p>
                          <p className="text-xs font-bold uppercase text-green-600 dark:text-emerald-500 mt-1">Total Allotted</p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-100 dark:border-orange-800/40 p-4 rounded-xl text-center">
                          <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{selectedLeave.balance.used}</p>
                          <p className="text-xs font-bold uppercase text-orange-700 dark:text-orange-500 mt-1">Previously Used</p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 p-4 rounded-xl text-center">
                          <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{selectedLeave.balance.remaining}</p>
                          <p className="text-xs font-bold uppercase text-blue-600 dark:text-blue-400 mt-1">Remaining Balance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!isAdmin && leaveBalance && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 shrink-0">
                      {[{ key: 'casual', label: 'Casual Leave',  color: 'blue' }, { key: 'sick', label: 'Sick Leave', color: 'purple' }, { key: 'earned', label: 'Earned Leave', color: 'teal' }].map(({ key, label, color }) => (
                        <div key={key} className={`bg-white dark:bg-[#161b22] rounded-xl border border-${color}-200 dark:border-[#30363d] p-4 shadow-xs`}>
                          <p className={`text-xs font-bold uppercase text-${color}-700 dark:text-${color}-400 mb-3`}>{label}</p>
                          <div className="flex items-end justify-between">
                            <div><p className={`text-3xl font-bold text-${color}-800 dark:text-white`}>{leaveBalance[`${key}_remaining`] ?? 0}</p><p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">remaining</p></div>
                            <div className="text-right text-xs text-gray-500 dark:text-slate-400"><p>{leaveBalance[`${key}_used`] ?? 0} used</p><p>of {leaveBalance[`${key}_total`] ?? 0} total</p></div>
                          </div>
                          <div className="mt-3 h-1.5 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className={`h-full bg-${color}-500 rounded-full transition-all`} style={{ width: `${leaveBalance[`${key}_total`] > 0 ? (leaveBalance[`${key}_used`] / leaveBalance[`${key}_total`]) * 100 : 0}%` }} /></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isAdmin && (
                    <StatsGrid stats={adminStats} cols={4} />
                  )}

                  <Toolbar
                    searchTerm={isAdmin ? searchTerm : undefined}
                    onSearch={isAdmin ? setSearchTerm : undefined}
                    searchPlaceholder="Search by employee name..."
                    tabs={['All', 'Pending', 'Approved', 'Rejected']}
                    activeTab={statusFilter}
                    onTabChange={setStatusFilter}
                  />

                  <TableCard>
                    <DataTable 
                      data={filteredData} 
                      columns={requestColumns} 
                      keyExtractor={(item) => item.id}
                      emptyMessage={isAdmin ? 'No leave requests found matching your criteria.' : 'You have no leave requests yet.'}
                    />
                  </TableCard>
                </>
              )}
            </>
          )}
        </>
      )}
    </PageLayout>
  );
}